import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Client } from '@/types/base';
import { AddLayoutFormData, ProjectOption, CreateLayoutResponse } from '../types';
import { getClients, getProjectsForClient, getLayoutTypes, createLayout } from '../api/layout-api';

export const useAddLayout = () => {
  const queryClient = useQueryClient();
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [layoutTypes, setLayoutTypes] = useState<string[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [formData, setFormData] = useState<AddLayoutFormData>({
    client: null,
    project: null,
    name: '',
    type: '',
  });

  // Load clients and layout types on mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoadingClients(true);
        const [clientsData, layoutTypesData] = await Promise.all([
          getClients(),
          getLayoutTypes(),
        ]);
        setClients(clientsData);
        setLayoutTypes(layoutTypesData);
      } catch (err) {
        setError('Failed to load clients');
        console.error('Error loading initial data:', err);
      } finally {
        setIsLoadingClients(false);
      }
    };

    loadInitialData();
  }, []);

  // Load projects when client changes
  useEffect(() => {
    const loadProjects = async () => {
      if (!formData.client) {
        setProjects([]);
        return;
      }

      try {
        setIsLoadingProjects(true);
        const projectsData = await getProjectsForClient(formData.client);
        setProjects(projectsData);
      } catch (err) {
        setError('Failed to load projects');
        console.error('Error loading projects:', err);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    loadProjects();
  }, [formData.client]);

  const handleClientChange = (clientId: number | null) => {
    setFormData((prev) => ({
      ...prev,
      client: clientId,
      project: null, // Reset project when client changes
    }));
    setProjects([]);
  };

  const handleProjectChange = (projectId: number | null) => {
    setFormData((prev) => ({
      ...prev,
      project: projectId,
    }));
  };

  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
    }));
  };

  const handleTypeChange = (type: 'Progress' | 'Asbuild' | 'Corridor' | '') => {
    setFormData((prev) => ({
      ...prev,
      type,
    }));
  };

  const handleSubmit = async (): Promise<CreateLayoutResponse | null> => {
    // Validation
    if (!formData.client || !formData.project || !formData.name || !formData.type) {
      setError('Please fill in all fields');
      return null;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setSuccessMessage(null);

      const response = await createLayout({
        client: formData.client,
        project: formData.project,
        name: formData.name,
        type: formData.type as 'Progress' | 'Asbuild' | 'Corridor',
      });

      if (response.success) {
        setSuccessMessage(response.message || 'Layout created successfully!');
        
        console.log('[useAddLayout] Invalidating layouts cache for project:', formData.project);
        
        // Invalidate layouts cache so the interactive viewer will refetch
        await queryClient.invalidateQueries({
          queryKey: ['layouts', String(formData.project)],
          exact: false,
        });
        
        console.log('[useAddLayout] Cache invalidated successfully');
        
        // Keep form data - don't reset after successful submission
      } else {
        setError(response.error || 'Failed to create layout');
      }

      return response;
    } catch (err: any) {
      const errorMessage = err?.data?.error || err?.message || 'An error occurred while creating the layout';
      setError(errorMessage);
      console.error('Error creating layout:', err);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      client: null,
      project: null,
      name: '',
      type: '',
    });
    setProjects([]);
    setError(null);
    setSuccessMessage(null);
  };

  return {
    // State
    clients,
    projects,
    layoutTypes,
    formData,
    isLoadingClients,
    isLoadingProjects,
    isSubmitting,
    error,
    successMessage,

    // Actions
    handleClientChange,
    handleProjectChange,
    handleNameChange,
    handleTypeChange,
    handleSubmit,
    resetForm,
    setError,
    setSuccessMessage,
  };
};


